import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Col, Container, Row } from "react-bootstrap";
import { doc, getDoc } from "firebase/firestore";
import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import ArrowCircleRightIcon from "@mui/icons-material/ArrowCircleRight";
import { ClimbingBoxLoader } from "react-spinners";

import {
  QuizAnswerBox,
  QuestionContainer,
  QuestionHeader,
  QuestionBody,
  ProgressBarContainer,
  ProgressBarProgress,
} from "./theme/QuizBox";
import { GradientSpan, H1, P2 } from "./theme/text/Headings";

import { auth, db } from "../services/firebase.config";
import { gradientColors, Grey, White05, Black, OffWhite } from "./theme/colors";
import LayoutQuiz from "./layout/QuizLayouts/layoutQuiz";
import remoteQuizService from "../services/RemoteQuizService";

import styled from "styled-components";

const UserLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 10px;
  text-align: center;
  opacity: 0.8;
`;

const AnswerComparisonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 15px;
  margin-top: 20px;
`;

const NavigationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: fixed;
  bottom: 80px; /* Position above bottom navigation */
  left: 0;
  right: 0;
  padding: 15px 20px;
  background-color: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(10px);
  z-index: 9; /* Below bottom navigation but above content */
  border-top: 1px solid rgba(255, 255, 255, 0.1);
`;

const NavigationButton = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  opacity: ${(props) => (props.disabled ? 0.3 : 1)};
  pointer-events: ${(props) => (props.disabled ? "none" : "auto")};
  transition: opacity 0.2s ease;
`;

const SharedAnswersComparison = () => {
  const { sharedId } = useParams();
  const navigate = useNavigate();
  const user = auth.currentUser;

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [sharedData, setSharedData] = useState(null);
  const [user1Answers, setUser1Answers] = useState({});
  const [user2Answers, setUser2Answers] = useState({});
  const [user1Profile, setUser1Profile] = useState(null);
  const [user2Profile, setUser2Profile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questionsData, setQuestionsData] = useState(null);
  const [quizConfig, setQuizConfig] = useState(null);

  // Get quiz type from shared data
  const quizType = sharedData?.quizName;
  const currentQuestion = questionsData
    ? questionsData[currentQuestionIndex]
    : null;
  const gradientConfig = quizType ? gradientColors[quizType] : null;

  useEffect(() => {
    const fetchSharedData = async () => {
      if (!sharedId || !user) {
        setError("Missing shared ID or user not authenticated");
        setLoading(false);
        return;
      }

      try {
        // Fetch shared quiz document
        const sharedDocRef = doc(db, "shared-quizzes", sharedId);
        const sharedDoc = await getDoc(sharedDocRef);

        if (!sharedDoc.exists()) {
          setError("Shared quiz not found");
          setLoading(false);
          return;
        }

        const sharedQuizData = sharedDoc.data();
        setSharedData(sharedQuizData);

        const currentQuizType = sharedQuizData.quizName;

        // Fetch quiz config (remote first, then static fallback)
        const config = await remoteQuizService.getQuizConfig(currentQuizType);
        setQuizConfig(config);

        // Fetch questions (remote first, then static fallback)
        let questions = await remoteQuizService.getQuizQuestions(
          currentQuizType
        );
        setQuestionsData(questions);

        // Determine which user is which
        const user1Uid = sharedQuizData.senderUid;
        const user2Uid = sharedQuizData.receiverUid;
        const user1QuizId = sharedQuizData.senderQuizId;
        const user2QuizId = sharedQuizData.receiverQuizId;

        // Fetch both users' profiles
        const [user1ProfileDoc, user2ProfileDoc] = await Promise.all([
          getDoc(doc(db, "public-profiles", user1Uid)),
          getDoc(doc(db, "public-profiles", user2Uid)),
        ]);

        setUser1Profile(
          user1ProfileDoc.exists() ? user1ProfileDoc.data() : null
        );
        setUser2Profile(
          user2ProfileDoc.exists() ? user2ProfileDoc.data() : null
        );

        // Fetch both users' quiz answers
        const [user1QuizDoc, user2QuizDoc] = await Promise.all([
          getDoc(doc(db, "quiz-answers", user1QuizId)),
          getDoc(doc(db, "quiz-answers", user2QuizId)),
        ]);

        if (user1QuizDoc.exists()) {
          setUser1Answers(user1QuizDoc.data().answers || {});
        }

        if (user2QuizDoc.exists()) {
          setUser2Answers(user2QuizDoc.data().answers || {});
        }

        setLoading(false);
      } catch (err) {
        console.error("Error loading shared quiz data:", err);
        setError("Failed to load shared quiz data");
        setLoading(false);
      }
    };

    fetchSharedData();
  }, [sharedId, user]);

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleNextQuestion = () => {
    if (questionsData && currentQuestionIndex < questionsData.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  const handleBackToResults = () => {
    if (quizConfig) {
      navigate(`${quizConfig.resultsRoute}?shared=${sharedId}`);
    }
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          background: Black,
        }}
      >
        <ClimbingBoxLoader color={OffWhite} />
      </div>
    );
  }

  if (
    error ||
    !sharedData ||
    !quizConfig ||
    !questionsData ||
    !currentQuestion
  ) {
    return (
      <LayoutQuiz>
        <Container>
          <Row className="mt-50">
            <Col className="text-align-center">
              <H1 color={Grey}>Unable to load comparison</H1>
              {error && (
                <p style={{ color: Grey, fontSize: "14px", marginTop: "10px" }}>
                  {error}
                </p>
              )}
              <button onClick={() => navigate("/home")} className="mt-20">
                Return Home
              </button>
            </Col>
          </Row>
        </Container>
      </LayoutQuiz>
    );
  }

  // Get user answers for current question
  const user1Answer = user1Answers[currentQuestion.id];
  const user2Answer = user2Answers[currentQuestion.id];

  // Find the actual answer text from the question's answers array
  const getAnswerText = (answerData) => {
    if (!answerData || !currentQuestion?.answers) return "No answer provided";

    const answerOption = currentQuestion.answers.find(
      (option) => option.answer === answerData.answer
    );

    return answerOption
      ? answerOption.copy
      : answerData.answer || "No answer provided";
  };

  // Determine user names (using same logic as results page)
  const user1Name = user1Profile?.username
    ? `@${user1Profile.username}`
    : user1Profile?.firstName || "User 1";
  const user2Name = user2Profile?.username
    ? `@${user2Profile.username}`
    : user2Profile?.firstName || "User 2";
  const currentUserIsUser1 = user.uid === sharedData.senderUid;

  // Always show current user's answer first
  const currentUserAnswer = currentUserIsUser1 ? user1Answer : user2Answer;
  const otherUserAnswer = currentUserIsUser1 ? user2Answer : user1Answer;
  const otherUserDisplayName = currentUserIsUser1 ? user2Name : user1Name;
  const currentUserColor = gradientConfig.background;
  const otherUserColor = gradientConfig.transitionBackground;

  // Create quiz header content
  const progressPercent =
    ((currentQuestionIndex + 1) / questionsData.length) * 100;

  const quizHeaderContent = (
    <>
      <ProgressBarContainer>
        <ProgressBarProgress
          $background={gradientConfig.background}
          $transitionBackground={gradientConfig.transitionBackground}
          width={`${progressPercent}%`}
        />
      </ProgressBarContainer>
      <Container>
        <Row style={{ position: "relative", alignItems: "center" }}>
          <div
            style={{
              position: "absolute",
              left: "20px",
              zIndex: 1,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "40px",
              height: "40px",
            }}
            onClick={handleBackToResults}
          >
            <ArrowCircleLeftIcon
              style={{
                color: White05,
                fontSize: "28px",
              }}
            />
          </div>
          <Col
            lg={{ offset: 4, span: 4 }}
            md={{ offset: 3, span: 6 }}
            sm={{ offset: 2, span: 8 }}
            xs={{ offset: 1, span: 10 }}
            className="text-align-center"
          >
            <P2 className="mb-0 justify-content-center" color={Grey}>
              Answer Comparison
            </P2>
          </Col>
        </Row>
      </Container>
    </>
  );

  return (
    <LayoutQuiz quizHeader={quizHeaderContent}>
      <Container>
        <Row className="mt-20">
          <Col
            lg={{ offset: 4, span: 4 }}
            md={{ offset: 3, span: 6 }}
            sm={{ offset: 2, span: 8 }}
            xs={{ offset: 1, span: 10 }}
            className="text-align-center"
          >
            <QuestionContainer className="mt-30 mb-20">
              <QuestionHeader>
                <P2 color={Grey}>
                  <GradientSpan
                    $background={gradientConfig.background}
                    $transitionBackground={gradientConfig.transitionBackground}
                  >
                    Question {currentQuestionIndex + 1} out of{" "}
                    {questionsData.length}
                  </GradientSpan>
                </P2>
              </QuestionHeader>
              <QuestionBody>
                <H1>{currentQuestion.questionText}</H1>
              </QuestionBody>
            </QuestionContainer>

            <AnswerComparisonContainer>
              {/* Current User's Answer - Always First */}
              <div>
                <UserLabel style={{ color: currentUserColor }}>
                  Your Answer
                </UserLabel>
                <QuizAnswerBox
                  $background={currentUserColor}
                  $transitionBackground={currentUserColor}
                  $selected={true}
                  $anyselected={true}
                  style={{ cursor: "default" }}
                >
                  <H1 className="mb-0">{getAnswerText(currentUserAnswer)}</H1>
                </QuizAnswerBox>
              </div>

              {/* Other User's Answer - Always Second */}
              <div>
                <UserLabel style={{ color: otherUserColor }}>
                  {otherUserDisplayName}
                </UserLabel>
                <QuizAnswerBox
                  $background={otherUserColor}
                  $transitionBackground={otherUserColor}
                  $selected={true}
                  $anyselected={true}
                  style={{ cursor: "default" }}
                >
                  <H1 className="mb-0">{getAnswerText(otherUserAnswer)}</H1>
                </QuizAnswerBox>
              </div>
            </AnswerComparisonContainer>

            <NavigationContainer>
              <NavigationButton
                disabled={currentQuestionIndex === 0}
                onClick={handlePreviousQuestion}
              >
                <ArrowCircleLeftIcon
                  style={{
                    color: White05,
                    fontSize: "40px",
                  }}
                />
              </NavigationButton>

              <div style={{ color: Grey, fontSize: "17px" }}>
                {currentQuestionIndex + 1} / {questionsData.length}
              </div>

              <NavigationButton
                disabled={currentQuestionIndex === questionsData.length - 1}
                onClick={handleNextQuestion}
              >
                <ArrowCircleRightIcon
                  style={{
                    color: White05,
                    fontSize: "40px",
                  }}
                />
              </NavigationButton>
            </NavigationContainer>
          </Col>
        </Row>
      </Container>
    </LayoutQuiz>
  );
};

export default SharedAnswersComparison;
